import { makeRng, type RNG } from "../rng.js";
import type { Vec2 } from "../types.js";
import type { ConvoyAmbushSite, ConvoyConfig, ConvoyWorld } from "./types.js";

/** World extent in metres. */
export const CONVOY_WORLD_W = 2400;
export const CONVOY_WORLD_H = 1400;

export type RoadSample = Vec2 & {
  /** Road tangent unit vector. */
  tx: number;
  ty: number;
  /** Road normal unit vector. */
  nx: number;
  ny: number;
};

export type ConvoyWorldInternal = ConvoyWorld & {
  posAt: (s: number) => RoadSample;
};

export function segSegIntersect(p: Vec2, p2: Vec2, q: Vec2, q2: Vec2): boolean {
  const r = { x: p2.x - p.x, y: p2.y - p.y };
  const s = { x: q2.x - q.x, y: q2.y - q.y };
  const rxs = r.x * s.y - r.y * s.x;
  if (Math.abs(rxs) < 1e-9) return false;
  const qp = { x: q.x - p.x, y: q.y - p.y };
  const t = (qp.x * s.y - qp.y * s.x) / rxs;
  const u = (qp.x * r.y - qp.y * r.x) / rxs;
  return t > 0 && t < 1 && u > 0 && u < 1;
}

export function pointInPoly(pt: Vec2, poly: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i];
    const b = poly[j];
    if (!a || !b) continue;
    if (a.y > pt.y !== b.y > pt.y && pt.x < ((b.x - a.x) * (pt.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

export function segHitsPoly(a: Vec2, b: Vec2, poly: Vec2[]): boolean {
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    if (p && q && segSegIntersect(a, b, p, q)) return true;
  }
  return pointInPoly({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }, poly);
}

/** Parametric t along (p→p2) where the segment crosses edge (q→q2), or null. */
function segSegT(p: Vec2, p2: Vec2, q: Vec2, q2: Vec2): number | null {
  const r = { x: p2.x - p.x, y: p2.y - p.y };
  const s = { x: q2.x - q.x, y: q2.y - q.y };
  const rxs = r.x * s.y - r.y * s.x;
  if (Math.abs(rxs) < 1e-9) return null;
  const qp = { x: q.x - p.x, y: q.y - p.y };
  const t = (qp.x * s.y - qp.y * s.x) / rxs;
  const u = (qp.x * r.y - qp.y * r.x) / rxs;
  return t > 0 && t < 1 && u >= 0 && u <= 1 ? t : null;
}

/**
 * Sub-intervals of segment a→b (parametric [t0,t1] pairs) that lie inside the
 * polygon.
 */
export function segPolyIntervals(a: Vec2, b: Vec2, poly: Vec2[]): Array<[number, number]> {
  const ts: number[] = [];
  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const q = poly[(i + 1) % poly.length];
    if (!p || !q) continue;
    const t = segSegT(a, b, p, q);
    if (t !== null) ts.push(t);
  }
  ts.sort((x, y) => x - y);
  const uniq: number[] = [];
  for (const t of ts) {
    const prev = uniq[uniq.length - 1];
    if (prev === undefined || t - prev > 1e-9) uniq.push(t);
  }
  const intervals: Array<[number, number]> = [];
  let inside = pointInPoly(a, poly);
  let start = 0;
  for (const t of uniq) {
    if (inside) {
      intervals.push([start, t]);
      inside = false;
    } else {
      start = t;
      inside = true;
    }
  }
  if (inside) intervals.push([start, 1]);
  return intervals;
}

/**
 * 2.5D line of sight: the ray from (a, heightA) to (b, heightB) must clear a
 * uniform canopy wherever its 2D track passes through a forest block. Ray
 * height is linear in t, so each crossing only needs its endpoints checked.
 */
export function losHeightClear(
  a: Vec2,
  heightA: number,
  b: Vec2,
  heightB: number,
  blocks: Vec2[][],
  canopy: number,
): boolean {
  for (const poly of blocks) {
    for (const [t0, t1] of segPolyIntervals(a, b, poly)) {
      const h0 = heightA + (heightB - heightA) * t0;
      const h1 = heightA + (heightB - heightA) * t1;
      if (Math.min(h0, h1) < canopy) return false;
    }
  }
  return true;
}

export function shrinkPoly(poly: Vec2[], f: number): Vec2[] {
  const c = poly.reduce(
    (s, p) => ({ x: s.x + p.x / poly.length, y: s.y + p.y / poly.length }),
    { x: 0, y: 0 },
  );
  return poly.map((p) => ({ x: c.x + (p.x - c.x) * f, y: c.y + (p.y - c.y) * f }));
}

function blob(cx: number, cy: number, radius: number, rng: RNG): Vec2[] {
  const n = 10 + Math.floor(rng() * 4);
  const poly: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const r = radius * (0.72 + rng() * 0.5);
    poly.push({ x: cx + Math.cos(a) * r * 1.15, y: cy + Math.sin(a) * r * 0.85 });
  }
  return poly;
}

export function buildConvoyWorld(cfg: ConvoyConfig): ConvoyWorldInternal {
  const rng = makeRng(cfg.seed * 104729 + 7);

  // Road polyline: a winding west-to-east route.
  const road: Vec2[] = [];
  const N = 140;
  const phase = rng() * Math.PI * 2;
  for (let i = 0; i <= N; i++) {
    const x = 70 + (i * (CONVOY_WORLD_W - 140)) / N;
    const y =
      CONVOY_WORLD_H * 0.52 +
      190 * Math.sin(x / 430 + phase) +
      70 * Math.sin(x / 165 + phase * 2.3);
    road.push({ x, y });
  }
  const cum: number[] = [0];
  let length = 0;
  for (let i = 1; i < road.length; i++) {
    const a = road[i - 1];
    const b = road[i];
    if (a && b) length += Math.hypot(b.x - a.x, b.y - a.y);
    cum.push(length);
  }

  function posAt(sIn: number): RoadSample {
    const s = Math.max(0, Math.min(length - 0.01, sIn));
    let lo = 0;
    let hi = cum.length - 1;
    while (hi - lo > 1) {
      const m = (lo + hi) >> 1;
      if ((cum[m] ?? 0) <= s) lo = m;
      else hi = m;
    }
    const cLo = cum[lo] ?? 0;
    const cHi = cum[hi] ?? cLo + 1;
    const f = (s - cLo) / (cHi - cLo);
    const a = road[lo] ?? { x: 0, y: 0 };
    const b = road[hi] ?? a;
    const tx = (b.x - a.x) / (cHi - cLo);
    const ty = (b.y - a.y) / (cHi - cLo);
    const len = Math.hypot(tx, ty) || 1;
    return {
      x: a.x + (b.x - a.x) * f,
      y: a.y + (b.y - a.y) * f,
      tx: tx / len,
      ty: ty / len,
      nx: -ty / len,
      ny: tx / len,
    };
  }

  // Ambush forest blocks, one or two per event depending on axes.
  const blocks: Vec2[][] = [];
  const sites: ConvoyAmbushSite[] = [];
  for (let e = 0; e < cfg.events; e++) {
    const s0 = length * (0.32 + (cfg.events > 1 ? (0.42 * e) / (cfg.events - 1) : 0) + rng() * 0.05);
    const sides = cfg.axes === 2 ? [1, -1] : [rng() < 0.5 ? 1 : -1];
    for (const side of sides) {
      const r = 150 + rng() * 110;
      const rp = posAt(s0 + rng() * 80 - 40);
      const cx = rp.x + rp.nx * side * (cfg.standoff + r);
      const cy = rp.y + rp.ny * side * (cfg.standoff + r);
      const poly = blob(cx, cy, r, rng);
      blocks.push(poly);
      sites.push({ s: s0, side, poly, cx, cy, r });
    }
  }

  // Filler forest away from the ambush standoff band.
  const nFill = Math.floor(cfg.density * 16);
  for (let i = 0; i < nFill; i++) {
    const s = length * (0.05 + rng() * 0.9);
    const side = rng() < 0.5 ? 1 : -1;
    const r = 90 + rng() * 150;
    const off = cfg.standoff + r + 60 + rng() * 420;
    const rp = posAt(s);
    blocks.push(blob(rp.x + rp.nx * side * off, rp.y + rp.ny * side * off, r, rng));
  }

  // Decorative meadows.
  const meadows: Array<{ x: number; y: number; r: number }> = [];
  for (let i = 0; i < 7; i++) {
    meadows.push({ x: rng() * CONVOY_WORLD_W, y: rng() * CONVOY_WORLD_H, r: 160 + rng() * 260 });
  }

  return { road, length, blocks, sites, meadows, posAt };
}
